import dicomParser from "dicom-parser";
import cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";

const ITEM_TAGS = new Set(["xfffee000", "xfffee00d", "xfffee0dd"]);

const NUMERIC_VRS = new Set([
  "IS",
  "US",
  "SS",
  "UL",
  "SL",
  "FL",
  "FD",
  "DS",
]);

const COMMON_DICOM_TAG_NAMES = {
  "00080005": "Specific Character Set",
  "00080008": "Image Type",
  "00080016": "SOP Class UID",
  "00080018": "SOP Instance UID",
  "00080020": "Study Date",
  "00080021": "Series Date",
  "00080022": "Acquisition Date",
  "00080023": "Content Date",
  "0008002A": "Acquisition DateTime",
  "00080030": "Study Time",
  "00080031": "Series Time",
  "00080032": "Acquisition Time",
  "00080050": "Accession Number",
  "00080060": "Modality",
  "00080070": "Manufacturer",
  "00080080": "Institution Name",
  "00080090": "Referring Physician's Name",
  "00081010": "Station Name",
  "00081030": "Study Description",
  "0008103E": "Series Description",
  "00081090": "Manufacturer's Model Name",
  "00100010": "Patient's Name",
  "00100020": "Patient ID",
  "00100030": "Patient's Birth Date",
  "00100040": "Patient's Sex",
  "00101010": "Patient's Age",
  "00180015": "Body Part Examined",
  "00180050": "Slice Thickness",
  "00181000": "Device Serial Number",
  "00181020": "Software Versions",
  "00181030": "Protocol Name",
  "0020000D": "Study Instance UID",
  "0020000E": "Series Instance UID",
  "00200010": "Study ID",
  "00200011": "Series Number",
  "00200012": "Acquisition Number",
  "00200013": "Instance Number",
  "00200032": "Image Position (Patient)",
  "00200037": "Image Orientation (Patient)",
  "00200052": "Frame of Reference UID",
  "00200062": "Image Laterality",
  "00201041": "Slice Location",
  "00280002": "Samples per Pixel",
  "00280004": "Photometric Interpretation",
  "00280008": "Number of Frames",
  "00280010": "Rows",
  "00280011": "Columns",
  "00280030": "Pixel Spacing",
  "00280100": "Bits Allocated",
  "00280101": "Bits Stored",
  "00280102": "High Bit",
  "00280103": "Pixel Representation",
  "00281050": "Window Center",
  "00281051": "Window Width",
  "00281052": "Rescale Intercept",
  "00281053": "Rescale Slope",
  "00220031": "Ophthalmic En Face Image Quality Sequence",
  "00220032": "Reference Coordinates",
  "00220039": "Ophthalmic Volumetric Properties Flag",
  "52009229": "Shared Functional Groups Sequence",
  "52009230": "Per-Frame Functional Groups Sequence",
  "7FE00010": "Pixel Data",
};

function formatTagId(tag) {
  return String(tag || "")
    .replace(/^x/i, "")
    .toUpperCase();
}

function isPrivateTag(tag) {
  const hex = formatTagId(tag);
  if (hex.length < 4) return false;
  const group = parseInt(hex.substring(0, 4), 16);
  return Number.isFinite(group) && group % 2 === 1;
}

function getDicomTagName(tag) {
  const id = formatTagId(tag);
  return (
    COMMON_DICOM_TAG_NAMES[id] ||
    (id.length === 8 ? `(${id.slice(0, 4)},${id.slice(4)})` : id)
  );
}

function parseScalarValue(str, vr) {
  if (!str) return "";
  if (!NUMERIC_VRS.has(vr)) return str;
  const n = Number(str);
  return Number.isFinite(n) ? n : str;
}

function parseElementStringValue(str, vr) {
  if (!str) return "";
  const separator = str.includes("\\") ? "\\" : "/";
  if (str.includes(separator)) {
    return str
      .split(separator)
      .map((part) => parseScalarValue(part, vr));
  }
  return parseScalarValue(str, vr);
}

function getElementTableValue(dataSet, element) {
  if (element.items?.length) {
    return element.items.map((item) =>
      dataSetToMetaTableRows(item.dataSet)
    );
  }

  if (!element.length) return "";

  if (
    element.length > 2048 ||
    element.vr === "OB" ||
    element.vr === "OW" ||
    element.vr === "UN"
  ) {
    if (element.tag === "x7fe00010" || element.tag === "x7fe00008") {
      return `[Pixel Data, ${element.length} bytes]`;
    }
    return `[${element.length} bytes]`;
  }

  try {
    const asString = dicomParser.explicitElementToString(dataSet, element);
    if (asString !== undefined) {
      return parseElementStringValue(asString, element.vr);
    }
  } catch (_error) {
    // Fall through to byte-length placeholder for implicit/unsupported VRs.
  }

  return `[${element.length} bytes]`;
}

export function dataSetToMetaTableRows(dataSet) {
  if (!dataSet?.elements) return [];

  return Object.keys(dataSet.elements)
    .filter((tag) => {
      const element = dataSet.elements[tag];
      if (!element || ITEM_TAGS.has(element.tag)) return false;
      if (isPrivateTag(tag)) return false;
      return true;
    })
    .sort()
    .map((tag) => {
      const element = dataSet.elements[tag];
      return {
        id: formatTagId(tag),
        vr: element.vr || "",
        name: getDicomTagName(tag),
        Value: getElementTableValue(dataSet, element),
      };
    });
}

export async function loadDataSetFromUrl(url) {
  if (!url) return null;
  try {
    return await cornerstoneWADOImageLoader.wadouri.dataSetCacheManager.load(
      url,
      cornerstoneWADOImageLoader.internal.xhrRequest
    );
  } catch (error) {
    console.error("Failed to load local DICOM dataset:", url, error);
    return null;
  }
}

export async function extractLocalDicomMetaTable(url, cachedDataSet = null) {
  const dataSet = cachedDataSet || (await loadDataSetFromUrl(url));
  if (!dataSet) return null;
  const rows = dataSetToMetaTableRows(dataSet);
  return rows.length > 0 ? rows : null;
}
